import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { SessionStep } from "@/hooks/useSessionSteps";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

interface SessionHistoryProps {
  steps: SessionStep[];
  isLoading: boolean;
  onStepClick: (step: SessionStep) => void;
}

export const SessionHistory = ({ steps, isLoading, onStepClick }: SessionHistoryProps) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Lade Historie...</div>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">
          Noch keine Antworten vorhanden
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-8rem)]">
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Session Historie
        </h2>
        {steps.map((step) => {
          const isExpanded = expandedSteps.has(step.id);
          return (
            <div
              key={step.id}
              className="border border-border rounded-lg bg-card transition-colors hover:bg-accent/5"
            >
              <div className="flex flex-col">
                <button
                  onClick={() => toggleStep(step.id)}
                  className="w-full p-4 text-left flex items-start gap-3 hover:bg-accent/10 transition-colors"
                >
                <div className="mt-1">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs">
                      Schritt {step.step_number}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {step.phase}.{step.subphase}
                    </Badge>
                    {step.clarity_score !== null && (
                      <Badge variant="outline" className="text-xs">
                        Klarheit: {step.clarity_score}%
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground line-clamp-2">
                    {step.question_text}
                  </p>
                </div>
              </button>
              {isExpanded && (
                <button
                  onClick={() => onStepClick(step)}
                  className="mx-4 mb-3 px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 border border-primary/20 rounded-md hover:bg-primary/5 transition-colors"
                >
                  Zu dieser Frage zurückkehren
                </button>
              )}
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                  {step.user_answer && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Deine Antwort:
                      </p>
                      <p className="text-sm text-foreground">
                        {step.user_answer}
                      </p>
                    </div>
                  )}
                  {step.answer_summary && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Zusammenfassung:
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {step.answer_summary}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};
